import * as segment from '@fullstory/proto/fs/api/segment';
import { DefinedEvent } from '@fullstory/proto/fs/services/search-settings';
import { pick } from 'lodash';
import {
  isDefinedEventSearchType,
  isSearchTypeWithField,
  isSearchTypeValueWithName,
} from './assertions';
import { NamedCustomEventType } from './types/event';
import { CustomFieldType, SearchType, UiOnlyKinds, UiOnlySubkinds } from './types/shared';
import { assertExhaustive } from '@fullstory/typeutils';

// Creates a string to uniquely identify a search type (and differentiates between named search types that share kind/subkind).
export function uniqueKey(t: SearchType): string {
  let entries: [string, string][];
  if (isSearchTypeValueWithName(t)) {
    entries = Object.entries({ ...pick(t, 'kind', 'subkind'), name: t.value.name });
  } else if (isDefinedEventSearchType(t)) {
    entries = Object.entries({ ...pick(t, 'kind', 'subkind'), id: t.value.id });
  } else if (isSearchTypeWithField(t)) {
    switch (t.field.type) {
      case CustomFieldType.Name:
        entries = Object.entries({ ...pick(t, 'kind', 'subkind'), fieldName: t.field.name });
        break;
      case CustomFieldType.Spec:
        entries = Object.entries({ ...pick(t, 'kind', 'subkind'), fieldSlug: t.field.slug });
        break;
      default:
        assertExhaustive(t.field);
    }
  } else {
    entries = Object.entries(pick(t, 'kind', 'subkind'));
  }
  return entries
    .sort((a, b) => b[0].localeCompare(a[0]))
    .reduce((prev, entry) => `${prev}#${entry[0]}:${entry[1]}`, '');
}

const analogousPathOperators = new Map<string, segment.UrlOperator.operator_keys>([
  [segment.UrlOperator.operator_keys.equals, segment.UrlOperator.operator_keys.containsToken],
  [segment.UrlOperator.operator_keys.notEquals, segment.UrlOperator.operator_keys.notContainsToken],
  [
    segment.UrlOperator.operator_keys.startsWith,
    segment.UrlOperator.operator_keys.containsTokenStartsWith,
  ],
]);

export function getAnalogousPathOperator(operator?: SearchType['subkind']) {
  if (operator && analogousPathOperators.has(operator)) {
    return analogousPathOperators.get(operator);
  }
  return operator;
}

export function namedCustomEventType(name: string): NamedCustomEventType {
  return {
    kind: UiOnlyKinds.orgEvent,
    subkind: UiOnlySubkinds.customNamed,
    value: {
      name,
    },
  };
}

// This should be synced up with cook-time matching in go/src/fs/services/julia/internal/definedeventmatching/filter/[primary, dependent].go
// Can we use tsexport to sync these up automatically?
export const definedEventCanBeCooked = (de: DefinedEvent): boolean =>
  // Check that every primary and dependent can be cooked
  de.events.every(
    event =>
      primaryEventCanBeCooked(event?.primary) &&
      event.deps.every(dep => dependentEventCanBeCooked(dep)),
  );

// Technically we wouldn't cook EventSet either, but that resolves to multiple events once it's set as a defined event
const primaryEventCanBeCooked = (event: segment.EventFilter | null): boolean =>
  !(
    event?.customError ||
    event?.userError ||
    event?.pageError ||
    event?.elemError ||
    event?.seenError
  );

const dependentEventCanBeCooked = (dep: segment.DependentEventFilter): boolean =>
  !(
    dep.pageDuration ||
    dep.pageActiveDuration ||
    dep.pageviewActiveDuration ||
    dep.pageviewInactiveDuration ||
    dep.pageviewTotalDuration ||
    dep.prevPageDefId ||
    dep.sessionExitPage ||
    dep.customApiErrorProperty ||
    // following are hidden from UI, but adding for safety
    dep.fsUserId ||
    dep.fsPageClusterId ||
    dep.fsSessionId
  );

export const primaryKindCanBeCooked = (kind: segment.EventFilter.filter_keys): boolean =>
  ![
    segment.EventFilter.filter_keys.customError,
    segment.EventFilter.filter_keys.userError,
    segment.EventFilter.filter_keys.pageError,
    segment.EventFilter.filter_keys.elemError,
    segment.EventFilter.filter_keys.seenError,
  ].includes(kind);

export const dependentKindCanBeCooked = (kind: segment.DependentEventFilter.filter_keys): boolean =>
  ![
    segment.DependentEventFilter.filter_keys.pageDuration,
    segment.DependentEventFilter.filter_keys.pageActiveDuration,
    segment.DependentEventFilter.filter_keys.pageviewActiveDuration,
    segment.DependentEventFilter.filter_keys.pageviewInactiveDuration,
    segment.DependentEventFilter.filter_keys.pageviewTotalDuration,
    segment.DependentEventFilter.filter_keys.prevPageDefId,
    segment.DependentEventFilter.filter_keys.sessionExitPage,
    segment.DependentEventFilter.filter_keys.customApiErrorProperty,
    segment.DependentEventFilter.filter_keys.fsUserId,
    segment.DependentEventFilter.filter_keys.fsPageClusterId,
  ].includes(kind);
